import React, { useState, useEffect } from 'react';
import { firestore } from '../../firebase/config';
import './ProfileForm.css';
import ProfileImageUpload from './ProfileImageUpload';

const ProfileForm = ({ practitioner, practitionerId, onProfileUpdate }) => {
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    email: '',
    phone: '',
    description: '',
    specialties: '',
    address: {
      street: '',
      city: '',
      zipCode: ''
    },
    cancellationPolicy: '',
    profileImage: ''
  });
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Initialiser le formulaire avec les données du praticien
  useEffect(() => {
    if (practitioner) {
      setFormData({
        name: practitioner.name || '',
        title: practitioner.title || '',
        email: practitioner.email || '',
        phone: practitioner.phone || '',
        description: practitioner.description || '',
        specialties: practitioner.specialties ? practitioner.specialties.join(', ') : '',
        address: {
          street: practitioner.address?.street || '',
          city: practitioner.address?.city || '',
          zipCode: practitioner.address?.zipCode || ''
        },
        cancellationPolicy: practitioner.cancellationPolicy || "Annulation gratuite jusqu'à 24h avant le rendez-vous. Passé ce délai, la séance sera due.",
        profileImage: practitioner.profileImage || ''
      });
    }
  }, [practitioner]);

  // Gérer les changements dans le formulaire
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name.startsWith('address.')) {
      const addressField = name.split('.')[1];
      setFormData({
        ...formData,
        address: {
          ...formData.address,
          [addressField]: value
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  // Soumettre le formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccessMessage('');
    setErrorMessage('');

    if (!practitionerId) {
      setErrorMessage('ID du praticien non trouvé');
      setLoading(false);
      return;
    }

    try {
      // Préparer les données pour la mise à jour
      const updateData = {
        name: formData.name,
        title: formData.title,
        email: formData.email,
        phone: formData.phone,
        description: formData.description,
        specialties: formData.specialties.split(',').map(s => s.trim()).filter(s => s),
        address: {
          street: formData.address.street,
          city: formData.address.city,
          zipCode: formData.address.zipCode
        },
        cancellationPolicy: formData.cancellationPolicy,
        profileImage: formData.profileImage,
        updatedAt: new Date()
      };

      // Mettre à jour le profil dans Firestore
      await firestore
        .collection('practitioners')
        .doc(practitionerId)
        .update(updateData);

      // Mettre à jour l'état local du parent
      if (onProfileUpdate) {
        onProfileUpdate({
          ...practitioner,
          ...updateData
        });
      }

      setSuccessMessage('Profil mis à jour avec succès');
    } catch (error) {
      console.error('Erreur lors de la mise à jour du profil:', error);
      setErrorMessage('Une erreur est survenue lors de la mise à jour du profil');
    } finally {
      setLoading(false);

      // Faire disparaître le message de succès après 3 secondes
      if (successMessage) {
        setTimeout(() => {
          setSuccessMessage('');
        }, 3000);
      }
    }
  };

  return (
    <div className="profile-form-container">
      {successMessage && (
        <div className="success-message">{successMessage}</div>
      )}

      {errorMessage && (
        <div className="error-message">{errorMessage}</div>
      )}

      <form onSubmit={handleSubmit} className="profile-form">
        <div className="form-section">
          <h4 className="section-title">Informations personnelles</h4>

          <div className="form-group">
            <label>Photo de profil</label>
            <ProfileImageUpload
              currentImage={formData.profileImage}
              onImageUpload={(imageUrl) => setFormData({ ...formData, profileImage: imageUrl })}
            />
          </div>


          <div className="form-row">
            <div className="form-group">
              <label htmlFor="name">Nom complet *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="form-control"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="title">Titre professionnel *</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className="form-control"
                placeholder="Ex: Naturopathe certifié"
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="email">Email *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="form-control"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone">Téléphone *</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="form-control"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="specialties">Spécialités (séparées par des virgules)</label>
            <input
              type="text"
              id="specialties"
              name="specialties"
              value={formData.specialties}
              onChange={handleChange}
              className="form-control"
              placeholder="Ex: Naturopathie, Phytothérapie, Relaxation"
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description professionnelle</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              className="form-control"
              rows="4"
              placeholder="Décrivez votre parcours et votre approche professionnelle"
            ></textarea>
          </div>
        </div>

        <div className="form-section">
          <h4 className="section-title">Adresse du cabinet</h4>

          <div className="form-group">
            <label htmlFor="address.street">Rue *</label>
            <input
              type="text"
              id="address.street"
              name="address.street"
              value={formData.address.street}
              onChange={handleChange}
              className="form-control"
              placeholder="Ex: 25 rue des Plantes"
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="address.zipCode">Code postal *</label>
              <input
                type="text"
                id="address.zipCode"
                name="address.zipCode"
                value={formData.address.zipCode}
                onChange={handleChange}
                className="form-control"
                placeholder="Ex: 75014"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="address.city">Ville *</label>
              <input
                type="text"
                id="address.city"
                name="address.city"
                value={formData.address.city}
                onChange={handleChange}
                className="form-control"
                placeholder="Ex: Paris"
                required
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h4 className="section-title">Politiques</h4>

          <div className="form-group">
            <label htmlFor="cancellationPolicy">Politique d'annulation</label>
            <textarea
              id="cancellationPolicy"
              name="cancellationPolicy"
              value={formData.cancellationPolicy}
              onChange={handleChange}
              className="form-control"
              rows="3"
              placeholder="Décrivez votre politique d'annulation"
            ></textarea>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Enregistrement...' : 'Enregistrer les modifications'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProfileForm;